import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCardModule } from '@angular/material/card';
import { MatDialogModule } from '@angular/material/dialog';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatMenuModule } from '@angular/material/menu';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { MatRadioModule } from '@angular/material/radio';
import { MatDividerModule } from '@angular/material/divider';
import { AgGridModule } from 'ag-grid-angular';
import { ClipboardModule } from '@angular/cdk/clipboard';
import { NgxJsonTreeviewComponent } from 'ngx-json-treeview';

import { NotificationService } from './notification-service/notification.service';
import { FilterPipe } from './pipes/filter.pipe';
import { CustomStepperComponent } from './stepper/stepper.component';
import { TestStatusBadgeComponent } from './test-status-badge/test-status-badge.component';
import { JobFunctionDropdownComponent } from './job-function-dropdown/job-function-dropdown.component';
import { BadgeComponent } from './badge/badge.component';
import { DateRangePickerComponent } from './date-range-picker/date-range-picker.component';
import { DateRangePickerCustomRangePanelComponent } from './date-range-picker/custom-range-panel/custom-range-panel.component';
import { JobLevelDropdownComponent } from './job-level-dropdown/job-level-dropdown.component';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { DarkModeToggleComponent } from './dark-mode-toggle/dark-mode-toggle.component';
import { SideImgDecorationComponent } from './side-img-decoration/side-img-decoration.component';
import { CreditUsageIndicatorComponent } from './credit-usage-indicator/credit-usage-indicator.component';
import { ChatUsageIndicatorComponent } from './chat-usage-indicator/chat-usage-indicator.component';
import { ClickOutsideDirective } from './directives/click-outside.directive';
import { CreditUsageService } from './credit-usage-indicator/credit-usage-service.service';
import { AlertComponent } from './alert/alert.component';
import { JobStartsAndEndsComponent } from './report-visualizations/job-starts-and-ends/job-starts-and-ends.component';
import { DemographicsComponent } from './report-visualizations/demographics/demographics.component';
import { HeadcountComparisonComponent } from './report-visualizations/headcount-comparison/headcount-comparison.component';
import { DemographicsCurrentComponent } from './report-visualizations/demographics-current/demographics-current.component';
import { TenureCurrentBreakdownComponent } from './report-visualizations/tenure-current-breakdown/tenure-current-breakdown.component';
import { FunctionalDistributionComponent } from './report-visualizations/functional-distribution/functional-distribution.component';
import { PrevNextFlowComponent } from './report-visualizations/prev-next-flow/prev-next-flow.component';
import { VizFromQuickbuildFactoryComponent } from './report-visualizations/viz-from-quickbuild-factory/viz-from-quickbuild-factory.component';
import { ChatDialogComponent } from './chat-dialog/chat-dialog.component';
import { MarkdownModule } from 'ngx-markdown';
import { DownloadStatusComponent } from './download-status/download-status.component';
import { DownloadStatusContainerComponent } from './download-status/download-status-container.component';
import { DownloadStatusService } from './download-status/download-status.service';
import { AgGridIconBtnsComponent } from './ag-grid-icon-btns/ag-grid-icon-btns.component';
import { DownloadFormatMatrixComponent } from './download-format-matrix/download-format-matrix.component';
import { TopValuesComparisonComponent } from './report-visualizations/top-values-comparison/top-values-comparison.component';
import { PeopleGridComponent } from './report-visualizations/people-grid/people-grid.component';
import { PeopleCountComponent } from './report-visualizations/people-count/people-count.component';
import { MAT_MOMENT_DATE_ADAPTER_OPTIONS } from '@angular/material-moment-adapter';
import { ShareThisViewComponent } from './share-this-view/share-this-view.component';
import { ShareThisViewDialogComponent } from './share-this-view/share-this-view-dialog.component';
import { JobsMovesComparisonComponent } from './report-visualizations/jobs-moves-comparison/jobs-moves-comparison.component';
import { OptionToggleComponent } from './components/option-toggle/option-toggle.component';
import { LocationMapComponent } from './report-visualizations/location-map/location-map.component';
import { HistoricalTenureComparisonComponent } from './report-visualizations/historical-tenure-comparison/historical-tenure-comparison.component';
import { CompanyListEditorComponent } from './company-list-editor/company-list-editor.component';
import { CompanySearchComponent } from './company-search/company-search.component';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatListModule } from '@angular/material/list';
import { PrevNextFlowComparisonComponent } from './report-visualizations/prev-next-flow-comparison/prev-next-flow-comparison.component';
import { TenureCohortComparisonComponent } from './report-visualizations/tenure-cohort-comparison/tenure-cohort-comparison.component';
import { PersonProfileComponent } from './person-profile/person-profile.component';
import { PersonJobHistoryGanttComponent } from './person-job-history-gantt/person-job-history-gantt.component';
import { PersonCellComponent } from './ag-grid-cell-renderers/person-cell.component';
import { JobCellComponent } from './ag-grid-cell-renderers/job-cell.component';
import { StoredItemsBrowserComponent } from './components/stored-items-browser/stored-items-browser.component';
import { DefaultItemRendererComponent } from './components/stored-items-browser/default-item-renderer.component';
import { SavedListItemRendererComponent } from './components/stored-items-browser/saved-list-item-renderer.component';
import { QuickBuildItemRendererComponent } from './components/stored-items-browser/quick-build-item-renderer.component';
import { OrgPickerDialogComponent } from './components/org-picker-dialog/org-picker-dialog.component';
import { RoleDropdownComponent } from './role-dropdown/role-dropdown.component';
import { QuickBuildDropdownComponent } from './quickbuild-dropdown/quickbuild-dropdown.component';
import { RoleDefinitionItemRendererComponent } from './components/role-definition-item-renderer/role-definition-item-renderer.component';
import { RoleBuilderComponent } from './role-builder/role-builder.component';
import { AdminMenuComponent } from './components/stored-items-browser/admin-menu.component';
import { AGGridSingleSelectFilterComponent } from './ag-grid-filters/ag-grid-single-select-filter';
import { AccessLinkCacheUpdateComponent } from './components/access-link-cache-update/access-link-cache-update.component';
import { TokenInputDialogComponent } from './components/token-input-dialog/token-input-dialog.component';
import { DeleteConfirmationComponent } from './components/delete-confirmation/delete-confirmation.component';
import { ThreadDetailsDialogComponent } from './components/thread-details-dialog/thread-details-dialog.component';
import { FollowupQuestionsFooterComponent } from './components/followup-questions-footer/followup-questions-footer.component';
import { FileSelectionDialogComponent } from './components/file-selection-dialog/file-selection-dialog.component';
import { ArtifactRendererComponent } from './components/artifact-renderer/artifact-renderer.component';
import { RotatingTextHeaderComponent } from './components/rotating-text-header/rotating-text-header.component';

@NgModule({
  declarations: [
    FilterPipe,
    CustomStepperComponent,
    TestStatusBadgeComponent,
    JobFunctionDropdownComponent,
    JobLevelDropdownComponent,
    BadgeComponent,
    DateRangePickerComponent,
    DateRangePickerCustomRangePanelComponent,
    DarkModeToggleComponent,
    SideImgDecorationComponent,
    CreditUsageIndicatorComponent,
    ChatUsageIndicatorComponent,
    ClickOutsideDirective,
    JobStartsAndEndsComponent,
    DemographicsComponent,
    HeadcountComparisonComponent,
    DemographicsCurrentComponent,
    TenureCurrentBreakdownComponent,
    FunctionalDistributionComponent,
    PrevNextFlowComponent,
    VizFromQuickbuildFactoryComponent,
    ChatDialogComponent,
    DownloadStatusComponent,
    DownloadStatusContainerComponent,
    AgGridIconBtnsComponent,
    DownloadFormatMatrixComponent,
    TopValuesComparisonComponent,
    PeopleGridComponent,
    PeopleCountComponent,
    ShareThisViewComponent,
    ShareThisViewDialogComponent,
    JobsMovesComparisonComponent,
    LocationMapComponent,
    HistoricalTenureComparisonComponent,
    CompanyListEditorComponent,
    PrevNextFlowComparisonComponent,
    TenureCohortComparisonComponent,
    PersonProfileComponent,
    PersonJobHistoryGanttComponent,
    PersonCellComponent,
    JobCellComponent,
    StoredItemsBrowserComponent,
    DefaultItemRendererComponent,
    SavedListItemRendererComponent,
    QuickBuildItemRendererComponent,
    OrgPickerDialogComponent,
    RoleDropdownComponent,
    QuickBuildDropdownComponent,
    RoleDefinitionItemRendererComponent,
    RoleBuilderComponent,
    AdminMenuComponent,
    AGGridSingleSelectFilterComponent,
    AccessLinkCacheUpdateComponent,
    TokenInputDialogComponent,
    ThreadDetailsDialogComponent,
    FollowupQuestionsFooterComponent,
    FileSelectionDialogComponent,
    ArtifactRendererComponent,
    RotatingTextHeaderComponent,
  ],
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    FormsModule,
    MatIconModule,
    MatDatepickerModule,
    MatFormFieldModule,
    MatNativeDateModule,
    MatCardModule,
    MatDialogModule,
    MatInputModule,
    MatButtonModule,
    MatSelectModule,
    MatCheckboxModule,
    MatTooltipModule,
    MatProgressBarModule,
    MatMenuModule,
    MatButtonToggleModule,
    MatChipsModule,
    MatRadioModule,
    MatDividerModule,
    MarkdownModule,
    AgGridModule,
    MatAutocompleteModule,
    MatProgressSpinnerModule,
    MatListModule,
    ClipboardModule,
    NgxJsonTreeviewComponent,
    AlertComponent,
    CompanySearchComponent,
    DeleteConfirmationComponent,
    OptionToggleComponent,
  ],
  exports: [
    ReactiveFormsModule,
    FilterPipe,
    CustomStepperComponent,
    TestStatusBadgeComponent,
    JobFunctionDropdownComponent,
    JobLevelDropdownComponent,
    BadgeComponent,
    MatIconModule,
    DateRangePickerComponent,
    DarkModeToggleComponent,
    SideImgDecorationComponent,
    CreditUsageIndicatorComponent,
    ChatUsageIndicatorComponent,
    ClickOutsideDirective,
    AlertComponent,
    JobStartsAndEndsComponent,
    DemographicsComponent,
    HeadcountComparisonComponent,
    DemographicsCurrentComponent,
    TenureCurrentBreakdownComponent,
    LocationMapComponent,
    FunctionalDistributionComponent,
    PrevNextFlowComponent,
    VizFromQuickbuildFactoryComponent,
    ChatDialogComponent,
    DownloadStatusContainerComponent,
    DownloadFormatMatrixComponent,
    TopValuesComparisonComponent,
    PeopleGridComponent,
    PeopleCountComponent,
    ShareThisViewComponent,
    ShareThisViewDialogComponent,
    JobsMovesComparisonComponent,
    OptionToggleComponent,
    HistoricalTenureComparisonComponent,
    CompanyListEditorComponent,
    CompanySearchComponent,
    PrevNextFlowComparisonComponent,
    TenureCohortComparisonComponent,
    PersonProfileComponent,
    StoredItemsBrowserComponent,
    DefaultItemRendererComponent,
    SavedListItemRendererComponent,
    QuickBuildItemRendererComponent,
    OrgPickerDialogComponent,
    RoleDefinitionItemRendererComponent,
    RoleDropdownComponent,
    QuickBuildDropdownComponent,
    RoleBuilderComponent,
    AdminMenuComponent,
    AGGridSingleSelectFilterComponent,
    AccessLinkCacheUpdateComponent,
    TokenInputDialogComponent,
    ThreadDetailsDialogComponent,
    FollowupQuestionsFooterComponent,
    DeleteConfirmationComponent,
    ArtifactRendererComponent,
    RotatingTextHeaderComponent,
  ],
  providers: [
    NotificationService,
    CreditUsageService,
    DownloadStatusService,
    { provide: MAT_MOMENT_DATE_ADAPTER_OPTIONS, useValue: { useUtc: true } },
    ShareThisViewComponent,
  ],
})
export class SharedModule {}
